<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import LineChart from 'shared/components/charts/LineChart.vue';
import Spinner from 'shared/components/Spinner.vue';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const isLoading = ref(false);
const chartData = ref({ labels: [], datasets: [] });

const fetchData = async () => {
  if (!props.filters?.from || !props.filters?.to) return;
  isLoading.value = true;
  try {
    const [handoffRes, resolutionRes] = await Promise.all([
      ReportsAPI.getReports({
        metric: 'bot_handoffs_count',
        from: props.filters.from,
        to: props.filters.to,
        groupBy: 'day',
      }),
      ReportsAPI.getReports({
        metric: 'bot_resolutions_count',
        from: props.filters.from,
        to: props.filters.to,
        groupBy: 'day',
      }),
    ]);

    const handoffs = handoffRes.data || [];
    const resolutions = resolutionRes.data || [];

    if (resolutions.length > 0) {
      chartData.value = {
        labels: resolutions.map(r => {
          const d = new Date(r.timestamp * 1000);
          return `${d.getMonth() + 1}/${d.getDate()}`;
        }),
        datasets: [
          {
            label: t('CEO_DASHBOARD.AI_PERFORMANCE.CONTAINMENT'),
            data: resolutions.map((r, i) => {
              const total = (r.value || 0) + (handoffs[i]?.value || 0);
              return total > 0 ? Math.round((r.value / total) * 100) : 0;
            }),
            borderColor: '#12A594',
            backgroundColor: 'rgba(18, 165, 148, 0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 3,
            pointBackgroundColor: '#12A594',
          },
        ],
      };
    } else {
      chartData.value = { labels: [], datasets: [] };
    }
  } catch {
    // silent
  } finally {
    isLoading.value = false;
  }
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      min: 0,
      max: 100,
      ticks: { callback: v => `${v}%` },
      grid: { drawOnChartArea: false },
    },
    x: {
      grid: { drawOnChartArea: false },
    },
  },
  plugins: { legend: { display: false } },
  animation: { duration: 0 },
};

const { startRefetching, stopRefetching } = useLiveRefresh(fetchData, 120000);

watch(
  () => props.filters,
  () => {
    fetchData();
    stopRefetching();
    startRefetching();
  },
  { deep: true }
);

onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div
    class="flex flex-col rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 px-6 py-5"
  >
    <h3 class="mb-4 text-sm font-medium text-n-slate-11 uppercase tracking-wider">
      {{ t('CEO_DASHBOARD.AI_PERFORMANCE.CONTAINMENT_TREND') }}
    </h3>
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>
    <div
      v-else-if="chartData.datasets.length === 0"
      class="flex items-center justify-center py-8"
    >
      <span class="text-sm text-n-slate-10">
        {{ t('CEO_DASHBOARD.BREAKDOWN.NO_DATA') }}
      </span>
    </div>
    <div v-else class="h-64">
      <LineChart :collection="chartData" :chart-options="chartOptions" />
    </div>
  </div>
</template>
